import "core-js/modules/es6.object.assign";
import "core-js/modules/es6.object.define-property";
import "core-js/modules/es6.symbol";
import "core-js/modules/es6.array.index-of";
import "core-js/modules/web.dom.iterable";
import "core-js/modules/es6.array.iterator";
import "core-js/modules/es6.object.to-string";
import "core-js/modules/es6.object.keys";
import "core-js/modules/es6.array.map";
import "core-js/modules/es6.array.for-each";
import "core-js/modules/es6.function.name";

function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

import React, { useMemo } from 'react';
import classNames from 'classnames';
import classes from '@nerdwallet/base-styles/classes';
import InputGroup, { shouldRenderInputGroup } from '@nerdwallet/react-input-group';
import { useId } from '@nerdwallet/react-accessibility';
import RadioOption from './radio-option';
/**
 * Util that determines whether the RadioOption is disabled, based on the
 * precedence of
 *
 * 1) if disabled is supplied at the top level of the RadioGroup, else
 * 2) if the individual RadioOption is marked as disabled, through options.disabled
 */

var isDisabled = function isDisabled(disabledGroup, disabledOption) {
  return disabledGroup || disabledOption;
};

var RadioGroup = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var alignRadioTop = props.alignRadioTop,
      className = props.className,
      defaultValue = props.defaultValue,
      disabled = props.disabled,
      inline = props.inline,
      nameProp = props.name,
      onChange = props.onChange,
      options = props.options,
      value = props.value,
      label = props.label,
      helpMessage = props.helpMessage,
      error = props.error,
      errorMessage = props.errorMessage,
      tooltip = props.tooltip,
      tooltipPlacement = props.tooltipPlacement,
      otherProps = _objectWithoutProperties(props, ["alignRadioTop", "className", "defaultValue", "disabled", "inline", "name", "onChange", "options", "value", "label", "helpMessage", "error", "errorMessage", "tooltip", "tooltipPlacement"]); // `name` is used to link related radio buttons together for keyboard
  // navigation. If the name isn't provided, we can use `useId()` to generate
  // one, since it doesn't really matter what the name is as long as it's a
  // globally-unique string.


  var name = useId(nameProp);
  var id = useId();
  var errorMessageId = "".concat(id, "-error-message");
  var helpMessageId = "".concat(id, "-help-message"); // It's safe to unconditionally render both IDs in `aria-describedby`, even
  // if there isn't a help/error message currently being shown. If
  // `aria-describedby` refers to an element that doesn't exist, it just skips it.

  var ariaDescribedByIds = "".concat(errorMessageId, " ").concat(helpMessageId);
  var changeHandlers = useMemo(function () {
    var newChangeHandlers = {};
    options.forEach(function (option) {
      newChangeHandlers[option.value] = function (event) {
        event.stopPropagation();

        if (onChange && event.target.value === 'on') {
          onChange({
            target: {
              value: option.value
            }
          });
        }
      };
    });
    return newChangeHandlers;
  }, // eslint-disable-next-line react-hooks/exhaustive-deps -- directive added automatically by Shepherd migration
  [options, value, onChange]);
  var willRenderInputGroup = shouldRenderInputGroup(props);
  var marginClassName = inline ? 'margin-right-4' : 'margin-bottom-2';
  var group = /*#__PURE__*/React.createElement("div", _extends({
    className: classNames(_defineProperty({}, className, className && !willRenderInputGroup)),
    ref: ref
  }, otherProps), options.map(function (option, i) {
    return /*#__PURE__*/React.createElement(RadioOption, {
      alignRadioTop: alignRadioTop,
      "aria-describedby": ariaDescribedByIds,
      checked: value != null ? option.value === value : undefined,
      className: classes(_defineProperty({}, marginClassName, i !== options.length - 1)),
      defaultChecked: defaultValue != null ? defaultValue === option.value : undefined,
      disabled: isDisabled(disabled, option.disabled),
      image: option.image,
      imageSrc: option.imageSrc,
      inline: inline,
      key: option.value,
      label: option.label,
      name: name,
      onChange: changeHandlers[option.value]
    });
  }));

  if (!willRenderInputGroup) {
    return group;
  }

  return /*#__PURE__*/React.createElement(InputGroup, {
    className: className,
    disabled: disabled,
    error: error,
    errorMessage: errorMessage,
    errorMessageId: errorMessageId,
    fieldset: true,
    helpMessage: helpMessage,
    helpMessageId: helpMessageId,
    input: group,
    label: label,
    tooltip: tooltip,
    tooltipPlacement: tooltipPlacement
  });
});
RadioGroup.displayName = 'RadioGroup'; // Hint to InputGroup and other consumers that a <fieldset> should be used.

RadioGroup.shouldUseFieldset = true;

if (process.env.NODE_ENV !== 'production') {
  // eslint-disable-next-line global-require
  var PropTypes = require('prop-types');

  RadioGroup.propTypes = {
    /**
     * A list of radio options.
     */
    options: PropTypes.arrayOf(PropTypes.shape({
      /** A component rendering an image. */
      image: PropTypes.node,

      /** A path to an image resource. */
      imageSrc: PropTypes.string,
      label: PropTypes.node.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]).isRequired
    })).isRequired,

    /** The currently selected value(s). */
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),

    /** Callback to be called when the selected value(s) change. */
    onChange: PropTypes.func,

    /**
     * Whether options should be rendered inline on one line (block by
     * default). This should only be used when there are only two simple
     * options (like "Yes" and "No").
     */
    inline: PropTypes.bool,

    /** Name attribute for <input> elements. */
    name: PropTypes.string,

    /** Initial value when state is uncontrolled. */
    defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),

    /** Flag to indicate if the radio options should not be selectable. */
    disabled: PropTypes.bool,

    /** Show the radio group in an error visual state. */
    error: PropTypes.bool,

    /** Optional label for the radio group. */
    label: PropTypes.string,

    /** Boolean flag to top-align the radio button when the radio text spans multiple lines. */
    alignRadioTop: PropTypes.bool,

    /** Optional text to be shown indicating the user is in an error state. */
    errorMessage: PropTypes.node,

    /** Optional help text shown below the radio group. */
    helpMessage: PropTypes.node,

    /** Optional tooltip shown via an icon next to the label. */
    tooltip: PropTypes.node,

    /** Positioning of tooltip. */
    tooltipPlacement: PropTypes.oneOf(['top', 'right', 'bottom', 'left']),

    /** Classes to add to the containing element. */
    className: PropTypes.string
  };
}

RadioGroup.defaultProps = {
  alignRadioTop: false,
  className: null,
  defaultValue: null,
  disabled: false,
  error: null,
  errorMessage: null,
  helpMessage: null,
  inline: false,
  label: null,
  name: null,
  onChange: function onChange() {},
  tooltip: null,
  tooltipPlacement: null,
  value: null
};
export default RadioGroup;